import { AccountType } from 'further-types/investor';
import {
  BasicUploadDisallowedColumnIds,
  UploadMode,
} from 'further-types/investor-investment-upload';
import {
  countryDropdownList,
  honorificsDropdownList,
  kycRiskRatingLabels,
  paymentEmailOptionsWithReminder,
} from 'further-ui/labels';
import {
  numberToCurrencyDefaultOptions,
  numberToCurrencyString,
} from 'further-ui/utils';
import { capitalize } from 'lodash';
import { decimalValidator } from '../helpers/validators';

const numberFormatter = {
  format: numberToCurrencyString,
  resolvedOptions: () => numberToCurrencyDefaultOptions,
};

const yesNoDropdownList = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const livedHere3YearsDropdownList = [
  { value: 'true', label: 'Yes' },
  {
    value: 'false',
    label: 'No – Please fill in on the right',
  },
];

export const groups = [
  {
    groupId: 'index',
    title: 'Row #',
    columns: [
      {
        columnId: 'index',
        width: 50,
        header: 'Row',
        style: {
          background: 'rgba(128, 128, 128, 0.1)',
          justifyContent: 'center',
        },
        className: '',
        nonEditable: true,
      },
    ],
  },
  {
    groupId: 'investor',
    title: 'Investor details (required for matching)',
    columns: [
      {
        columnId: 'first_name',
        width: 150,
        header: 'First name',
        placeholder: 'e.g. Thomas',
        required: true,
        resizable: true,
      },
      {
        columnId: 'last_or_corporate_name',
        width: 180,
        header: 'Last/registered name',
        placeholder: 'e.g. Smith',
        required: true,
        resizable: true,
      },
      {
        columnId: 'identity_date',
        width: 170,
        header: 'Birth/registration date',
        placeholder: 'DD/MM/YYYY',
        required: true,
        type: 'custom_date',
        format: new Intl.DateTimeFormat('en-GB'),
        resizable: true,
      },
      {
        columnId: 'account_type',
        width: 150,
        header: 'User type',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: Object.values(AccountType).map((value) => ({
          value,
          label: capitalize(value).replace(/_/g, ' '),
        })),
        resizable: true,
      },
      {
        columnId: 'external_investor_id',
        width: 150,
        header: 'External investor ID',
        placeholder: 'If none, leave blank',
        resizable: true,
        className: 'dark-border-right',
        style: {
          border: {
            right: {
              style: 'solid',
              width: '2px',
            },
          },
        },
      },
    ],
  },
  {
    groupId: 'investment',
    title: 'Investment details',
    columns: [
      {
        columnId: 'investment_amount',
        width: 150,
        header: 'Investment amount',
        placeholder: 'e.g. 1000.00',
        required: true,
        type: 'number',
        format: numberFormatter,
        validator: decimalValidator,
        resizable: true,
      },
      {
        columnId: 'investment_date',
        width: 150,
        header: 'Investment date',
        placeholder: 'DD/MM/YYYY',
        required: true,
        type: 'custom_date',
        format: new Intl.DateTimeFormat('en-GB'),
        resizable: true,
      },
      {
        columnId: 'tranche',
        width: 150,
        header: 'Tranche',
        placeholder: 'Please choose',
        required: true,
        isDisabled: false,
        isOpen: false,
        type: 'custom_dropdown',
        values: [],
        resizable: true,
      },
      {
        columnId: 'adviser',
        width: 250,
        header: 'Adviser',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        isOpen: false,
        values: [],
        resizable: true,
      },
      {
        columnId: 'additional_linked_adviser',
        width: 250,
        header: 'Additional linked adviser',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        isOpen: false,
        values: [],
        resizable: true,
      },
      {
        columnId: 'advice_fee',
        width: 150,
        header: 'Advice fee',
        placeholder: 'e.g. 1000.00',
        type: 'number',
        format: numberFormatter,
        validator: decimalValidator,
        resizable: true,
      },
      {
        columnId: 'performance_fee_template_id',
        width: 225,
        header: 'Performance fee override',
        placeholder: 'Use fund default performance fee preset',
        type: 'custom_dropdown',
        values: [],
        resizable: true,
      },
      {
        columnId: 'core_fee_template_id',
        width: 225,
        header: 'Core fee override',
        placeholder: 'Use fund default core fee preset',
        type: 'custom_dropdown',
        values: [],
        resizable: true,
      },
      {
        columnId: 'use_cash_balance',
        width: 150,
        header: 'Use cash balance',
        placeholder: 'Please choose',
        required: false,
        type: 'custom_dropdown',
        values: yesNoDropdownList,
        resizable: true,
      },
      {
        columnId: 'cash_balance',
        width: 150,
        header: 'Cash balance',
        required: false,
        type: 'number',
        format: numberFormatter,
        validator: decimalValidator,
        resizable: true,
      },
      {
        columnId: 'payment_email_status',
        width: 180,
        header: 'Payment and email status',
        placeholder: 'Please choose',
        required: true,
        type: 'custom_dropdown',
        values: paymentEmailOptionsWithReminder,
        resizable: true,
      },
      {
        columnId: 'payment_date',
        width: 170,
        header: 'Payment date',
        placeholder: 'DD/MM/YYYY',
        type: 'custom_date',
        format: new Intl.DateTimeFormat('en-GB'),
        resizable: true,
      },
      {
        columnId: 'paid_to',
        width: 250,
        header: 'Paid to',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        isOpen: false,
        values: [],
        resizable: true,
      },
      {
        columnId: 'payment_reference',
        width: 200,
        header: 'Payment reference',
        resizable: true,
      },
      {
        columnId: 'external_investment_id',
        width: 200,
        header: 'External investment ID',
        resizable: true,
        className: 'dark-border-right',
        style: {
          border: {
            right: {
              style: 'solid',
              width: '2px',
            },
          },
        },
      },
    ],
  },
  {
    groupId: 'identity',
    title:
      'Please fill in these columns if this is a new individual investor († = required). If this is a legal entity, please scroll to the right to find the legal entity data columns.',
    columns: [
      {
        columnId: 'title',
        width: 150,
        header: 'Title',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: honorificsDropdownList,
        resizable: true,
      },
      {
        columnId: 'email',
        width: 150,
        header: 'Initial user email †',
        placeholder: 'e.g. tom@smith.com',
        type: 'email',
        resizable: true,
      },
      {
        columnId: 'middle_name',
        width: 150,
        header: 'Middle name',
        placeholder: 'e.g. James',
        resizable: true,
      },
      {
        columnId: 'phone',
        width: 200,
        header: 'Phone †',
        placeholder: 'Numbers, + and () only',
        resizable: true,
      },
      {
        columnId: 'new_investor_adviser',
        width: 150,
        header: 'Default adviser',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: [],
        resizable: true,
      },
      {
        columnId: 'primary_tax_id',
        width: 150,
        header: 'Tax ID 1 (e.g. NINO) †',
        placeholder: 'e.g. TS101010C',
        resizable: true,
      },
      {
        columnId: 'secondary_tax_id',
        width: 150,
        header: 'Tax ID 2 (e.g. UTR)',
        placeholder: 'e.g. 1234567890',
        resizable: true,
      },
      {
        columnId: 'bank_account_name',
        width: 150,
        header: 'Bank account name',
        placeholder: 'e.g. Mr T Smith',
        resizable: true,
      },
      {
        columnId: 'bank_name',
        width: 150,
        header: 'Bank name',
        placeholder: 'e.g. Natwest',
        resizable: true,
      },
      {
        columnId: 'bank_account_no',
        width: 150,
        header: 'Bank account no',
        placeholder: 'e.g. 00000000',
        resizable: true,
      },
      {
        columnId: 'sort_code',
        width: 150,
        header: 'Sort code',
        placeholder: 'e.g. 00-00-00',
        type: 'custom_sortcode',
        resizable: true,
      },
      {
        columnId: 'iban',
        width: 150,
        header: 'IBAN/non-UK account number',
        placeholder: 'IBAN',
        resizable: true,
      },
      {
        columnId: 'swift',
        width: 150,
        header: 'SWIFT/BIC',
        placeholder: 'SWIFT/BIC',
        resizable: true,
      },
      {
        columnId: 'international_bank_code',
        width: 150,
        header: 'Intl bank code',
        placeholder: 'Intl bank code',
        resizable: true,
      },
      {
        columnId: 'profession',
        width: 150,
        header: 'Job title',
        placeholder: 'e.g. Lawyer',
        resizable: true,
      },
      {
        columnId: 'sector',
        width: 200,
        header: 'Sector',
        placeholder: 'e.g. Financial Services',
        resizable: true,
      },
      {
        columnId: 'nationality_1',
        width: 150,
        header: 'Nationality 1 †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'nationality_2',
        width: 150,
        header: 'Nationality 2',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'nationality_3',
        width: 150,
        header: 'Nationality 3',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'is_us_person',
        width: 150,
        header: 'US Person?',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: yesNoDropdownList,
        resizable: true,
      },
      {
        columnId: 'claiming_eis',
        width: 150,
        header: 'Claiming EIS? †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: yesNoDropdownList,
        resizable: true,
      },
      {
        columnId: 'tax_residency_1',
        width: 150,
        header: 'Tax residency 1 †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'tax_residency_2',
        width: 150,
        header: 'Tax residency 2',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'tax_residency_3',
        width: 150,
        header: 'Tax residency 3',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'address_line_1',
        width: 150,
        header: 'Address line 1 †',
        placeholder: '',
        resizable: true,
      },
      {
        columnId: 'address_line_2',
        width: 150,
        header: 'Address line 2',
        placeholder: '',
        resizable: true,
      },
      {
        columnId: 'city',
        width: 150,
        header: 'City †',
        placeholder: '',
        resizable: true,
      },
      {
        columnId: 'postcode',
        width: 150,
        header: 'Postcode †',
        placeholder: 'e.g. SW1 1AA',
        resizable: true,
      },
      {
        columnId: 'country',
        width: 150,
        header: 'Country †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'run_aml',
        width: 150,
        header: 'Run AML',
        placeholder: 'No',
        type: 'custom_dropdown',
        values: yesNoDropdownList,
        resizable: true,
      },
      {
        columnId: 'risk_rating',
        width: 150,
        header: 'Risk rating',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: kycRiskRatingLabels,
        resizable: true,
      },
      {
        columnId: 'prev_address_a_lived_here_3_years',
        width: 220,
        header: 'Has lived here for 3 years?',
        placeholder: 'Yes / No',
        type: 'custom_dropdown',
        values: livedHere3YearsDropdownList,
        resizable: true,
      },
    ],
  },
  {
    groupId: 'new_investor_legal_entity_details',
    title:
      'Please fill in these columns if this is a new legal entity user († = required)',
    columns: [
      {
        columnId: 'registration_number',
        width: 150,
        header: 'Registration number †',
        placeholder: 'Registration number',
        resizable: true,
      },
      {
        columnId: 'registration_state',
        width: 150,
        header: 'Registration state †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'registered_tax_number',
        width: 180,
        header: 'Registered tax number',
        placeholder: 'Registered tax number',
        resizable: true,
      },
      {
        columnId: 'key_contact_email',
        width: 180,
        header: 'Key contact email †',
        placeholder: 'e.g. contact@company.com',
        type: 'email',
        resizable: true,
      },
      {
        columnId: 'key_contact_first_name',
        width: 200,
        header: 'Key contact first name †',
        placeholder: 'Key contact first name',
        resizable: true,
      },
      {
        columnId: 'key_contact_surname',
        width: 200,
        header: 'Key contact surname †',
        placeholder: 'Key contact surname',
        resizable: true,
      },
      {
        columnId: 'key_contact_phone_number',
        width: 200,
        header: 'Key contact phone number †',
        placeholder: 'Key contact phone number',
        resizable: true,
      },
      {
        columnId: 'new_investor_legal_entity_adviser',
        width: 150,
        header: 'Default adviser',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: [],
        resizable: true,
      },
      {
        columnId: 'leu_bank_account_name',
        width: 150,
        header: 'Bank account name',
        placeholder: 'e.g. Mr T Smith',
        resizable: true,
      },
      {
        columnId: 'leu_bank_name',
        width: 150,
        header: 'Bank name',
        placeholder: 'e.g. Natwest',
        resizable: true,
      },
      {
        columnId: 'leu_bank_account_no',
        width: 150,
        header: 'Bank account no',
        placeholder: 'e.g. 00000000',
        resizable: true,
      },
      {
        columnId: 'leu_sort_code',
        width: 150,
        header: 'Sort code',
        placeholder: 'e.g. 00-00-00',
        type: 'custom_sortcode',
        resizable: true,
      },
      {
        columnId: 'leu_iban',
        width: 150,
        header: 'IBAN/non-UK account number',
        placeholder: 'IBAN',
        resizable: true,
      },
      {
        columnId: 'leu_swift',
        width: 150,
        header: 'SWIFT/BIC',
        placeholder: 'SWIFT/BIC',
        resizable: true,
      },
      {
        columnId: 'leu_international_bank_code',
        width: 150,
        header: 'Intl bank code',
        placeholder: 'Intl bank code',
        resizable: true,
      },
      {
        columnId: 'leu_tax_residency_1',
        width: 150,
        header: 'Tax domicile 1 †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'leu_tax_residency_2',
        width: 150,
        header: 'Tax domicile 2',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'leu_address_line_1',
        width: 150,
        header: 'Address line 1 †',
        placeholder: '',
        resizable: true,
      },
      {
        columnId: 'leu_address_line_2',
        width: 150,
        header: 'Address line 2',
        placeholder: '',
        resizable: true,
      },
      {
        columnId: 'leu_city',
        width: 150,
        header: 'City †',
        placeholder: '',
        resizable: true,
      },
      {
        columnId: 'leu_postcode',
        width: 150,
        header: 'Postcode †',
        placeholder: 'e.g. SW1 1AA',
        resizable: true,
      },
      {
        columnId: 'leu_country',
        width: 150,
        header: 'Country †',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'leu_prev_address_a_lived_here_3_years',
        width: 220,
        header: 'Has lived here for 3 years?',
        placeholder: 'Yes / No',
        type: 'custom_dropdown',
        values: livedHere3YearsDropdownList,
        resizable: true,
      },
    ],
  },
  {
    groupId: 'address_history',
    title:
      'Please use these columns to fill in 3 years of previous addresses (for new investors)',
    columns: [
      {
        columnId: 'prev_address_a_line_1',
        width: 200,
        header: 'Previous address A line 1',
        placeholder: 'e.g. 123 Main St',
        resizable: true,
      },
      {
        columnId: 'prev_address_a_line_2',
        width: 200,
        header: 'Previous address A line 2',
        placeholder: 'e.g. Apt 4',
        resizable: true,
      },
      {
        columnId: 'prev_address_a_city',
        width: 200,
        header: 'Previous address A city',
        placeholder: 'e.g. London',
        resizable: true,
      },
      {
        columnId: 'prev_address_a_postcode',
        width: 200,
        header: 'Previous address A postcode',
        placeholder: 'e.g. SW1 1AA',
        resizable: true,
      },
      {
        columnId: 'prev_address_a_country',
        width: 200,
        header: 'Previous address A country',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'prev_address_b_line_1',
        width: 200,
        header: 'Previous address B line 1',
        placeholder: 'e.g. 123 Main St',
        resizable: true,
      },
      {
        columnId: 'prev_address_b_line_2',
        width: 200,
        header: 'Previous address B line 2',
        placeholder: 'e.g. Apt 4',
        resizable: true,
      },
      {
        columnId: 'prev_address_b_city',
        width: 200,
        header: 'Previous address B city',
        placeholder: 'e.g. London',
        resizable: true,
      },
      {
        columnId: 'prev_address_b_postcode',
        width: 200,
        header: 'Previous address B postcode',
        placeholder: 'e.g. SW1 1AA',
        resizable: true,
      },
      {
        columnId: 'prev_address_b_country',
        width: 200,
        header: 'Previous address B country',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
      {
        columnId: 'prev_address_c_line_1',
        width: 200,
        header: 'Previous address C line 1',
        placeholder: 'e.g. 123 Main St',
        resizable: true,
      },
      {
        columnId: 'prev_address_c_line_2',
        width: 200,
        header: 'Previous address C line 2',
        placeholder: 'e.g. Apt 4',
        resizable: true,
      },
      {
        columnId: 'prev_address_c_city',
        width: 200,
        header: 'Previous address C city',
        placeholder: 'e.g. London',
        resizable: true,
      },
      {
        columnId: 'prev_address_c_postcode',
        width: 200,
        header: 'Previous address C postcode',
        placeholder: 'e.g. SW1 1AA',
        resizable: true,
      },
      {
        columnId: 'prev_address_c_country',
        width: 200,
        header: 'Previous address C country',
        placeholder: 'Please choose',
        type: 'custom_dropdown',
        values: countryDropdownList,
        resizable: true,
      },
    ],
  },
];

type UploadColumnGroup = (typeof groups)[number];
type UploadColumn = UploadColumnGroup['columns'][number];

const BASIC_HIDDEN_COLUMN_IDS = new Set<string>(BasicUploadDisallowedColumnIds);

const toAdviserEmailColumn = (
  columnId: string,
  header: string,
  width: number,
) => ({
  columnId,
  width,
  header,
  placeholder: 'e.g. adviser@firm.com',
  type: 'email',
  resizable: true,
});

export const getGroupsForUploadMode = (mode: UploadMode) => {
  if (mode === UploadMode.Full) {
    return groups.map((g: UploadColumnGroup) => ({
      ...g,
      columns: g.columns.map((c: UploadColumn) => ({ ...c })),
    }));
  }

  return groups.map((group: UploadColumnGroup) => ({
    ...group,
    columns: group.columns
      .filter((col: UploadColumn) => !BASIC_HIDDEN_COLUMN_IDS.has(col.columnId))
      .map((col: UploadColumn) => {
        if (col.columnId === 'adviser') {
          return toAdviserEmailColumn(col.columnId, 'Adviser email', col.width);
        }
        if (col.columnId === 'additional_linked_adviser') {
          return toAdviserEmailColumn(
            col.columnId,
            'Additional linked adviser email',
            col.width,
          );
        }
        return { ...col };
      }),
  }));
};

/**
 * Build the columns from the groups (needed by the React grid component)
 * - flatten the groups into a single array of columns
 */
export const columns = groups
  .map((group) => group.columns.map((col) => ({ ...col })))
  .flat(1);

/**
 * Build the column groups from the groups (needed by the React grid component)
 * - add a totalSize property to each group
 * - this is used to set the width of the group header
 * - the width of the group header is the sum of the widths of the columns in the group
 */
export const columnGroups = groups.map((group) => ({
  groupId: group.groupId,
  title: group.title,
  totalSize: group.columns.reduce((acc, col) => acc + col.width, 0),
}));

export const getColumnsForUploadMode = (mode: UploadMode) =>
  getGroupsForUploadMode(mode)
    .map((group) => group.columns.map((col) => ({ ...col })))
    .flat(1);

export const getColumnGroupsForUploadMode = (mode: UploadMode) =>
  getGroupsForUploadMode(mode).map((group) => ({
    groupId: group.groupId,
    title: group.title,
    totalSize: group.columns.reduce((acc, col) => acc + col.width, 0),
  }));
